import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Form } from 'antd';
import { ContentHeader } from '../layout/ContentHeader';
import { DarwinSpin } from 'components/darwin-ui';
import { useTranslation } from 'react-i18next';
import { FormNameSection } from './FormNameSection';
import { EditDataCaptureFormSection } from './EditDataCaptureFormSection';
import { transformSectionsAndFields } from './utils';
import {
  DataCaptureEditFormData,
  SaveDataCaptureFormPayload,
} from 'types/dataCaptureTypes';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import {
  useGetDataCaptureFormById,
  useUpdateDataCaptureFormById,
} from 'api/services/dataCaptureService';
import { useUI } from 'contexts/UiContext';
import { extractQueryData } from 'utils/services';
import { ROUTES } from 'routes/routes';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import styles from './DataCapture.module.css';
import { ErrorType } from 'types/genericTypes';

type EditDataCaptureFormProps = {
  formAction?: 'edit' | 'view';
};

export const EditDataCaptureForm = ({
  formAction = 'edit',
}: EditDataCaptureFormProps) => {
  const { id: dataCaptureFormId, traceability, processFlow } = useParams();
  const { openNotification } = useFloatNotification();
  const { selectedCompany } = useUI();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isViewForm = formAction === 'view';
  const handleNavigateProcessFlow = () => {
    navigate(
      generatePath(ROUTES.PROCESSES.MAPS.MAP.BLOCK.EDIT, {
        id: traceability || '',
        processId: processFlow || '',
        tab: '2',
      })
    );
  };

  const handleCancel = () => {
    if (traceability && processFlow) {
      handleNavigateProcessFlow();
    } else {
      navigate(ROUTES.DATA.CAPTURE_FORMS.LIST);
    }
  };

  const methods = useForm<DataCaptureEditFormData>({
    defaultValues: {
      formName: '',
      formDescription: '',
      sections: [],
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors },
    reset,
  } = methods;

  // submit data
  const {
    mutateAsync: updateDataCaptureForm,
    isPending: isUpdateDataCaptureFormPending,
  } = useUpdateDataCaptureFormById();

  const onSubmit = async () => {
    const formData = getValues();
    const payload: SaveDataCaptureFormPayload = {
      ...formData,
      sections: formData.sections.map(section => ({
        ...section,
        fields: section.fields.map(
          ({ field_type, is_required, ...field }) => field
        ),
      })),
    };
    try {
      await updateDataCaptureForm({
        payload,
        companyId: selectedCompany || '',
        processFormId: dataCaptureFormId || '',
      });
      openNotification(
        t('success'),
        t('edit-data-capture-form.edit-form-success'),
        'success'
      );
      if (traceability && processFlow) {
        handleNavigateProcessFlow();
      } else {
        navigate(ROUTES.DATA.CAPTURE_FORMS.LIST);
      }
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('edit-data-capture-form.edit-form-error'),
          'error'
        );
      }
    }
  };

  // get initial data
  const getDataCaptureFormByIdQuery = useGetDataCaptureFormById(
    selectedCompany || '',
    dataCaptureFormId || ''
  );

  const getDataCaptureFormByIdData = extractQueryData(
    getDataCaptureFormByIdQuery,
    undefined
  );

  useEffect(() => {
    if (getDataCaptureFormByIdData && selectedCompany) {
      const { form_name, form_description, sections } =
        getDataCaptureFormByIdData;

      const payload = {
        formName: form_name,
        formDescription: form_description,
        sections,
      };
      const formattedPayload = transformSectionsAndFields(payload);
      reset(formattedPayload);
    }
  }, [getDataCaptureFormByIdData, reset, selectedCompany]);

  return (
    <>
      <DarwinSpin spinning={getDataCaptureFormByIdQuery.isPending} fullscreen />

      <ContentHeader
        title={
          isViewForm
            ? t('edit-data-capture-form.view-title')
            : t('edit-data-capture-form.edit-title')
        }
        onCancel={() => handleCancel()}
        onSave={handleSubmit(onSubmit)}
        saveDisabled={isUpdateDataCaptureFormPending}
      />

      <section className={styles.screenContainer}>
        <FormProvider {...methods}>
          <FormNameSection isViewForm={isViewForm} />
          <Form onFinish={handleSubmit(onSubmit)} layout="vertical">
            {getValues()?.sections?.map((section, index) => (
              <EditDataCaptureFormSection
                key={section.section_name}
                section={section}
                sectionIndex={index}
                control={control}
                errors={errors}
                isViewForm={isViewForm}
              />
            ))}
          </Form>
        </FormProvider>
      </section>
    </>
  );
};
